import React, { useState } from "react";
import { useSharedSelector } from "../../store/SharedProvider";
import { selectRooms } from "../../store/room/room-selectors";
import ZoneSelection from "../Room/Selectors/ZoneSelection";

interface QuickCleanZoneTabProps {
  /** The room the robot is currently in – used as the initial room selection. */
  initialRoomId?: string | null;
  selectedZoneIds: string[];
  fixedOrder: boolean;
  onZoneSelectionChanged: (ids: string[], fixedOrder: boolean) => void;
  /**
   * Whether to show the room-selector dropdown when multiple rooms share the
   * same floor.  Defaults to `true`.  Pass `false` in contexts where the room
   * is already determined externally (e.g. the mission-edit dialog).
   */
  allowRoomSelection?: boolean;
}

/**
 * Inline zone-selection panel for the quick-clean dashboard dialog.
 *
 * Reads rooms directly from the shared Redux state so it works identically
 * on the robot webapp and the Sphere (cloud) frontend.
 * Room data and cleaning zones are fetched inside ZoneSelection.
 */
const QuickCleanZoneTab: React.FC<QuickCleanZoneTabProps> = ({
  initialRoomId,
  selectedZoneIds,
  fixedOrder,
  onZoneSelectionChanged,
  allowRoomSelection = true,
}) => {
  const allRooms = useSharedSelector((state) => selectRooms(state));

  const currentFloorId = allRooms.find((r) => r.id === initialRoomId)?.floorId;
  const roomsOnFloor = currentFloorId != null
    ? allRooms.filter(
        (r) => r.floorId === currentFloorId && !r.isArchived && r.secondaryStation == null,
      )
    : [];

  const [selectedRoomId, setSelectedRoomId] = useState<string>(
    initialRoomId ?? roomsOnFloor[0]?.id ?? "",
  );

  const handleRoomChange = (roomId: string) => {
    setSelectedRoomId(roomId);
    // Clear zone selection when the room changes.
    onZoneSelectionChanged([], fixedOrder);
  };

  return (
    <ZoneSelection
      roomId={selectedRoomId}
      selectedZoneIds={selectedZoneIds}
      fixedOrder={fixedOrder}
      onZoneSelectionChanged={onZoneSelectionChanged}
      roomsForSelection={allowRoomSelection ? roomsOnFloor : undefined}
      selectedRoomId={selectedRoomId}
      onRoomChange={handleRoomChange}
    />
  );
};

export default QuickCleanZoneTab;



